import { useQuery } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { TrendingUp, TrendingDown, Shield, AlertTriangle, Award } from 'lucide-react'

interface SafetyMetrics {
  overall_score: number
  score_change: number
  total_events: number
  critical_events: number
  harsh_braking: number
  speeding: number
  rapid_acceleration: number
  safe_drivers: number
  at_risk_drivers: number
}

export function SafetyScoreCard() {
  const { data, isLoading } = useQuery({
    queryKey: ['safety-metrics'],
    queryFn: async () => {
      const res = await fetch('/api/safety/metrics')
      if (!res.ok) throw new Error('Failed to fetch safety metrics')
      return res.json() as Promise<SafetyMetrics>
    },
    refetchInterval: 60000, // Refresh every minute
  })

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-500'
    if (score >= 75) return 'text-yellow-500'
    return 'text-red-500'
  }

  const getScoreLabel = (score: number) => {
    if (score >= 90) return 'Excellent'
    if (score >= 75) return 'Good'
    if (score >= 60) return 'Fair'
    return 'Needs Improvement'
  }

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center text-muted-foreground">Loading safety metrics...</div>
        </CardContent>
      </Card>
    )
  }

  const metrics = data || {
    overall_score: 0,
    score_change: 0,
    total_events: 0,
    critical_events: 0,
    harsh_braking: 0,
    speeding: 0,
    rapid_acceleration: 0,
    safe_drivers: 0,
    at_risk_drivers: 0
  }

  const isImproving = metrics.score_change > 0

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      {/* Overall Safety Score */}
      <Card className="col-span-2">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Fleet Safety Score
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <div className={`text-5xl font-bold ${getScoreColor(metrics.overall_score)}`}>
                {metrics.overall_score}
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                {getScoreLabel(metrics.overall_score)}
              </p>
            </div>
            <div className="text-right">
              <div className={`flex items-center gap-1 text-sm ${isImproving ? 'text-green-600' : 'text-red-600'}`}>
                {isImproving ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
                <span>{isImproving ? '+' : ''}{metrics.score_change} points</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">vs. last month</p>
            </div>
          </div>
          <div className="mt-4">
            <Progress value={metrics.overall_score} className="h-3" />
          </div>
        </CardContent>
      </Card>

      {/* Driver Performance */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Award className="h-4 w-4" />
            Driver Performance
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Safe Drivers</span>
            <Badge variant="default" className="bg-green-600">
              {metrics.safe_drivers}
            </Badge>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">At Risk</span>
            <Badge variant="destructive">
              {metrics.at_risk_drivers}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Safety Events */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            Safety Events (30d)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Harsh Braking</span>
            <span className="font-medium">{metrics.harsh_braking}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Speeding</span>
            <span className="font-medium">{metrics.speeding}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Rapid Accel.</span>
            <span className="font-medium">{metrics.rapid_acceleration}</span>
          </div>
          <div className="pt-2 border-t">
            <div className="flex justify-between text-sm font-semibold">
              <span>Total Events</span>
              <span>{metrics.total_events}</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
